/**
 * @fileoverview Input/output schemas for the style-aligner-v2 agent.
 *
 * This agent APPLIES a style guide and writing-sample corpus to a (typically
 * neutral) chapter draft, producing voice-rich prose while preserving every
 * beat, dialogue line, action, and character voice.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 *
 * Primary style inputs:
 * - styleGuide: structured rubric (sliders, banned/recommended phrases, tone)
 * - writingStyleSamples: corpus of named author samples (voice exemplars)
 * - writingStyleSample: ad-hoc single sample (UI override, takes precedence
 *   over the corpus when the user supplies one at runtime)
 * - charactersCurrent: per-character voice, personality, core values,
 *   emotional state — owns dialogue and behavior.
 */
export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.styleGuide.schema)
  .merge(AGENT_SCHEMA_OBJECTS.charactersCurrent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.chapterContent.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema)
  .merge(AGENT_SCHEMA_OBJECTS.draftingHistory.schema)
  .merge(AGENT_SCHEMA_OBJECTS.writingStyleSample.schema)
  .merge(AGENT_SCHEMA_OBJECTS.writingStyleSamples.schema);

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Unified output schema for content-producing agents.
 */
export const OutputSchema = z.object({
  commentary: z.string().describe(
    'Brief commentary explaining which voice signals from the samples and style guide were applied, where the biggest texture additions landed, and how per-character voices were honored (2-3 sentences). ' +
    'This will be recorded in the drafting history.'
  ),
  content: z.string().describe(
    'The fully re-voiced chapter with prose written in the target style, formatted as a single HTML string.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
